import {useInfiniteQuery} from 'react-query'
import {useRouter} from 'next/router'
import type {Elements, IContentItem} from '@kontent-ai/delivery-sdk'
import {useContext, useEffect, useMemo, useState} from 'react'
import {Pagination} from '@/products/flexible-web-toolkit/components/pagination'
import {Button} from '@/products/flexible-web-toolkit/components/button/button'
import {SkeletonLoader} from '@/products/flexible-web-toolkit/components/skeleton-loader'
import {pushToDataLayer} from '@/utilities/analytics'
import type {
	Block,
	TaxonomySpeciesContentItem,
	Tersed,
} from '@/services/kontent-ai/types'
import {fetchListerItems} from '@/services/lister-items/client'
import type {ListerItemsQueryKey} from '@/products/flexible-web-toolkit/blocks/module-coupon-lister/types'
import {useAuth} from '@/products/auth/auth-provider'
import {
	ResourceCardBlock,
	type ResourceCardContentItem,
} from '@/products/flexible-web-toolkit/blocks/resource-card'
import {textChecker} from '@/utilities/rich-text'
import {GlobalContext} from '@/services/global-client-state'
import type {ListerSortingContentItem, SortingCriterion} from '../lister-model'
import {dropDownItems, sortItems} from '../lister-helper'
import {ListerSorting} from '../lister-sorting'

export type ResourceCardListerContentItem = IContentItem<{
	initialItemsNumber: Elements.NumberElement
	subsequentItemsNumber: Elements.NumberElement
	snippetSpacingSpacing: Elements.MultipleChoiceElement
	usePagination: Elements.MultipleChoiceElement
	paginationString: Elements.TextElement
	buttonText: Elements.TextElement
	nextButtonText: Elements.TextElement
	prevButtonText: Elements.TextElement
	itemsOrderingOptions: Elements.MultipleChoiceElement
	resourceCardListerSorting: Elements.LinkedItemsElement<ListerSortingContentItem>
}>

export const ResourceCardListerBlock: Block<ResourceCardListerContentItem> = ({
	block,
	page,
	globalConfig,
	...context
}) => {
	const serverRenderedBlock = block as typeof block & {
		initialItems?: Tersed<ResourceCardContentItem>[]
		initialTotalCount?: number
	}
	const router = useRouter()
	const gs = useContext(GlobalContext)
	const authState = useAuth()

	const {page: currentPage} = router.query

	const [pageNumber, setPageNumber] = useState(0)

	const serverRenderedInitialItems = serverRenderedBlock.initialItems

	const [sortingCriterion, setSortingCriterion] = useState<
		SortingCriterion | undefined
	>()

	const {buttonText} = block.elements

	const nextButtonText =
		'nextButtonText' in block.elements && block.elements.nextButtonText
			? block.elements.nextButtonText
			: 'Next'

	const prevButtonText =
		'prevButtonText' in block.elements && block.elements.prevButtonText
			? block.elements.prevButtonText
			: 'Prev'

	const subsequentResourceNumber = block.elements.subsequentItemsNumber ?? 6

	const initialItemsNumber = block.elements.initialItemsNumber ?? 6

	const [limitNumberWithSubSeq, setLimitNumberWithSubSeq] =
		useState(initialItemsNumber)

	const speciesCodename =
		'filterByTaxonomy' in page.elements
			? (
					page.elements
						.filterByTaxonomy as Tersed<TaxonomySpeciesContentItem>[]
				)[0]?.elements.species[0]?.codename
			: undefined

	const speciesFilter = [
		{
			taxonomy: 'species',
			terms: [speciesCodename],
		},
	]
		.map((item) => ({
			...item,
			terms: item.terms.filter(Boolean),
		}))
		.filter((item) => item.terms.length > 0)

	const [filters, setFilters] = useState<
		{
			taxonomy: string
			terms: string[]
		}[]
	>()

	useEffect(() => {
		const resourcesFilterOptions = gs?.[0].resourcesFilterOptions ?? {}
		const updatedFilters = Object.entries(resourcesFilterOptions).map(
			([taxonomy, options]) => ({
				taxonomy,
				terms: (() => {
					if ('checkboxOptions' in options) {
						return options.checkboxOptions
							.filter((opt) => opt.checked)
							.map((opt) => opt.codename)
					}
					return [options.value]
				})(),
			})
		)
		setFilters([...speciesFilter, ...updatedFilters])
		setLimitNumberWithSubSeq(initialItemsNumber)

		// eslint-disable-next-line react-hooks/exhaustive-deps -- intentional as speciesFilter is static and doesn't change after the initial render, so it's safe to omit it from the dependency array.
	}, [gs])

	const usePagination = block.elements.usePagination[0]?.codename === 'yes'
	const userRoles = authState.userDetails.userRoles ?? []
	const {isLoading, isFetching, fetchNextPage, data} = useInfiniteQuery({
		queryKey: [
			'resourceLister',
			{
				codename: 'resource_card',
				locale: router.locale ?? '',
				filters: JSON.stringify(filters),
				orderBy:
					block.elements.itemsOrderingOptions[0]?.codename ??
					undefined,
				hideElementCodename: 'hide_from_lister',
				skip:
					(Number(currentPage?.toString() ?? 1) - 1) *
					initialItemsNumber,
				limit: limitNumberWithSubSeq,
				roles: userRoles.join(','),
			},
		] as ListerItemsQueryKey,
		queryFn: (opts) =>
			fetchListerItems<Tersed<ResourceCardContentItem>>(opts),
		getNextPageParam: () => {
			return pageNumber
		},
		staleTime: 10 * (60 * 1000),
		cacheTime: 15 * (60 * 1000),
		enabled: globalConfig.elements.cdcKey
			? (authState.ready && !authState.authenticated) ||
				(authState.ready &&
					authState.authenticated &&
					Boolean(authState.userDetails.id))
			: true,
	})

	const fetchedResourceCards = useMemo(
		() => data?.pages.flatMap((item) => item.items),
		[data]
	)
	const resourceCards = useMemo(
		() =>
			sortItems<Tersed<ResourceCardContentItem>>(
				[...(fetchedResourceCards ?? serverRenderedInitialItems ?? [])],
				sortingCriterion,
				router.locale
			),
		[
			fetchedResourceCards,
			router.locale,
			serverRenderedInitialItems,
			sortingCriterion,
		]
	)
	const totalCount =
		data?.pages[0]?.pagination.totalCount ??
		serverRenderedBlock.initialTotalCount ??
		0
	const shouldShowInitialSkeleton = !data && resourceCards.length === 0
	const shouldShowResourceCards =
		((!isLoading && data) || resourceCards.length > 0) &&
		resourceCards.length > 0

	function getHasNextPage(): boolean {
		const displayedItems = resourceCards.length
		return displayedItems < totalCount
	}

	const handleSortingLister = (
		e: React.ChangeEvent<HTMLSelectElement>
	): void => {
		setSortingCriterion(e.target.value as SortingCriterion)
	}

	const onPageChange = (nextPage: number): void => {
		const currentQuery = new URLSearchParams(
			router.asPath.split('?')[1] || ''
		)
		currentQuery.set('page', nextPage.toString())

		void router.push({
			pathname: `${router.locale ?? ''}${router.asPath.split('?')[0]}`,
			query: currentQuery.toString(),
		})
	}

	return (
		<div>
			{shouldShowInitialSkeleton ? (
				<div className="mx-auto my-2 grid max-w-screen-xl grid-cols-1 gap-10 px-3 sm:grid-cols-2 sm:px-0 md:grid-cols-3">
					<SkeletonLoader amount={initialItemsNumber} />
				</div>
			) : null}
			{shouldShowResourceCards ? (
				<div className="mx-auto flex max-w-screen-xl flex-wrap items-center justify-between">
					<p
						className="mb-4 px-4 text-center font-bold sm:text-left"
						data-kontent-element-codename="pagination_string"
					>
						{block.elements.paginationString
							.replaceAll(
								'{{X}}',
								resourceCards.length.toString()
							)
							.replaceAll('{{Y}}', totalCount.toString())}
					</p>
					{block.elements.resourceCardListerSorting[0] ? (
						<ListerSorting
							className="w-full px-4 sm:w-auto"
							codename={
								block.elements.resourceCardListerSorting[0]
									?.system.codename
							}
							dropDownData={dropDownItems(
								block.elements.resourceCardListerSorting[0]
							)}
							id={
								block.elements.resourceCardListerSorting[0]
									?.system.id || 'sorting_resource_listers'
							}
							label={textChecker(
								block.elements.resourceCardListerSorting[0]
									?.elements.defaultSortingText.value || '',
								'Sort By'
							)}
							onChange={handleSortingLister}
						/>
					) : null}
				</div>
			) : null}
			{shouldShowResourceCards ? (
				<div className="mx-auto flex max-w-screen-xl flex-wrap justify-center">
					{resourceCards
						.filter(authState.checkUserRoleForBlock)
						.map((item) => (
							<div
								className="my-2 flex w-full flex-wrap justify-center sm:w-1/2 md:w-1/3"
								key={item.system.id}
							>
								<ResourceCardBlock
									block={item}
									globalConfig={globalConfig}
									page={page}
									{...context}
								/>
							</div>
						))}
				</div>
			) : null}
			{isFetching && data ? (
				<div className="mx-auto my-2 grid max-w-screen-xl grid-cols-1 gap-10 px-3 sm:grid-cols-2 sm:px-0 md:grid-cols-3">
					<SkeletonLoader amount={subsequentResourceNumber} />
				</div>
			) : null}
			{usePagination ? (
				<Pagination
					activeIndex={Number(currentPage) || 1}
					amountOfPages={Math.ceil(totalCount / initialItemsNumber)}
					nextButtonText={<span>{nextButtonText}</span>}
					onPageChange={onPageChange}
					pagesToShow={3}
					prevButtonText={<span>{prevButtonText}</span>}
					showLastPage
				/>
			) : (
				buttonText &&
				getHasNextPage() && (
					<div
						className="my-10 text-center"
						data-kontent-element-codename="button_text"
					>
						<Button
							onClick={async () => {
								setLimitNumberWithSubSeq(
									limitNumberWithSubSeq +
										subsequentResourceNumber
								)
								await fetchNextPage()
								setPageNumber((cur) => cur + 1)
								pushToDataLayer({
									event: 'cta_click',
									cta_name: buttonText,
									cta_category: 'button',
								})
							}}
							type="button"
						>
							{buttonText}
						</Button>
					</div>
				)
			)}
		</div>
	)
}
